import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

/**
 * NotFoundPage — Proper SEO 404 handling (Feb 12, 2026).
 *
 * SEO audit finding #5: previously the SPA had `<Route path="*" element={<Navigate to="/" replace />} />`
 * which redirected ALL unknown URLs to homepage → Google saw HTTP 200 with
 * homepage content = "Soft 404". This page emits explicit noindex + 404
 * signals so Google recognises unknown routes as genuine 404s.
 *
 * Signals emitted:
 *   1. <meta name="robots" content="noindex, nofollow"> — prevents indexing
 *   2. <meta name="prerender-status-code" content="404"> — signals prerender services
 *   3. Empty <title> "Page Not Found · ViratAstro" — clear intent
 *   4. On mount, sets document.title so subsequent navigation is clean
 *
 * Note: True HTTP 404 status codes for SPAs require server-side/edge rewrite
 * rules. This client-side 404 handles indexing correctly for Googlebot which
 * respects the noindex meta and prerender-status-code hint.
 */
export default function NotFoundPage() {
  useEffect(() => {
    // Best-effort: signal a 404 to any bots reading the DOM
    document.title = 'Page Not Found · ViratAstro';
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="not-found-page">
      <Helmet>
        <title>Page Not Found · ViratAstro</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="prerender-status-code" content="404" />
        <meta name="description" content="This page was not found on ViratAstro. Please use navigation or search to find what you're looking for." />
      </Helmet>

      <Header />

      <main className="flex-1 flex items-center justify-center px-4 py-24">
        <div className="max-w-xl w-full text-center">
          <div className="mb-8">
            <p className="font-heading text-8xl sm:text-9xl text-gold-gradient font-medium leading-none">404</p>
            <div className="divine-divider mt-6 max-w-[180px] mx-auto" />
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl text-text-main mb-4" data-testid="not-found-heading">
            यह पृष्ठ नहीं मिला
          </h1>
          <p className="text-text-muted mb-2">
            The page you are looking for does not exist or has been moved.
          </p>
          <p className="text-text-muted/70 text-sm mb-10">
            जिस पेज को आप ढूँढ रहे हैं वह उपलब्ध नहीं है या स्थानांतरित कर दिया गया है।
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="btn-gold inline-flex items-center gap-2 text-base"
              data-testid="not-found-home-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Home पर जाएँ
            </Link>
            <Link
              to="/blog"
              className="btn-gold-outline inline-flex items-center gap-2 text-base"
              data-testid="not-found-blog-btn"
            >
              <Search className="w-4 h-4" /> Blogs Browse करें
            </Link>
          </div>

          {/* Popular pages — helps recover the user + strengthens internal linking */}
          <div className="mt-14 pt-8 border-t border-gold/10">
            <p className="text-xs uppercase tracking-[0.25em] text-gold/60 font-semibold mb-4">
              लोकप्रिय पृष्ठ · Popular pages
            </p>
            <div className="flex flex-wrap justify-center gap-2 text-sm">
              {[
                { to: '/kundli',          label: 'Free Kundli' },
                { to: '/daily-rashifal',  label: 'Daily Rashifal' },
                { to: '/gun-milan',       label: 'Gun Milan' },
                { to: '/daily-panchang',  label: 'Panchang' },
                { to: '/poocho-guruji',   label: 'पूछो Guruji' },
                { to: '/faq',             label: 'FAQ' },
                { to: '/upay',            label: 'Upay' },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-3 py-1.5 rounded-full border border-gold/25 bg-white/[0.03] text-text-muted hover:text-gold hover:border-gold/50 transition-colors"
                  data-testid={`not-found-link-${l.to.replace(/\//g, '-')}`}
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
